import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Admin.css';

const ManageApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = {
        status: filter !== 'all' ? filter : undefined,
        search: searchTerm || undefined
      };
      const response = await api.get('/admin/applications', { params });
      setApplications(response.data || []);
      setPagination(response.pagination || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchApplications();
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await api.patch(`/admin/applications/${applicationId}/status`, {
        status: newStatus
      });
      toast.success('Application status updated');
      fetchApplications();
    } catch (error) {
      console.error('Error updating application:', error);
      toast.error('Failed to update application status');
    }
  };

  const handleDelete = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await api.delete(`/admin/applications/${applicationId}`);
      toast.success('Application deleted successfully');
      fetchApplications();
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Failed to delete application');
    }
  };

  const viewDetails = async (applicationId) => {
    try {
      const response = await api.get(`/admin/applications/${applicationId}`);
      setSelectedApplication(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching application details:', error);
      toast.error('Failed to fetch application details');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      submitted: 'badge-info',
      reviewed: 'badge-warning',
      shortlisted: 'badge-primary',
      interviewed: 'badge-secondary',
      offered: 'badge-success',
      rejected: 'badge-danger',
      withdrawn: 'badge-dark'
    };
    return badges[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Manage Applications</h1>
          <p>View and manage all job applications</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by candidate name or job title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Applications
          </button>
          <button
            className={`filter-tab ${filter === 'submitted' ? 'active' : ''}`}
            onClick={() => setFilter('submitted')}
          >
            Submitted
          </button>
          <button
            className={`filter-tab ${filter === 'reviewed' ? 'active' : ''}`}
            onClick={() => setFilter('reviewed')}
          >
            Reviewed
          </button>
          <button
            className={`filter-tab ${filter === 'shortlisted' ? 'active' : ''}`}
            onClick={() => setFilter('shortlisted')}
          >
            Shortlisted
          </button>
          <button
            className={`filter-tab ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
        </div>
      </div>

      {/* Applications Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Job Title</th>
              <th>Company</th>
              <th>Status</th>
              <th>Applied Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No applications found
                </td>
              </tr>
            ) : (
              applications.map((application) => (
                <tr key={application._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {application.candidate?.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontWeight:'500', lineHeight:'1'}}>{application.candidate?.name}</div>
                        <div className="text-muted">{application.candidate?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{application.jobId?.title || 'N/A'}</td>
                  <td>{application.companyId?.name || 'N/A'}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(application.status)}`}>
                      {application.status}
                    </span>
                  </td>
                  <td>{new Date(application.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => viewDetails(application._id)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(application._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-sm"
            disabled={pagination.page === 1}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="btn btn-sm"
            disabled={pagination.page === pagination.pages}
          >
            Next
          </button>
        </div>
      )}

      {/* Application Details Modal */}
      {showModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{borderBottom:'none', marginBottom:'0px'}}>
              <h2>Application Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body application_detail">
              <div className="detail-section">
                <h3>Candidate Information</h3>
                <p><strong>Name:</strong> {selectedApplication.candidateId?.name}</p>
                <p><strong>Email:</strong> {selectedApplication.candidateId?.email}</p>
                <p><strong>Phone:</strong> {selectedApplication.candidateId?.phone || 'N/A'}</p>
              </div>
              <div className="detail-section">
                <h3>Job Information</h3>
                <p><strong>Title:</strong> {selectedApplication.jobId?.title}</p>
                <p><strong>Company:</strong> {selectedApplication.companyId?.name}</p>
              </div>
              <div className="detail-section">
                <h3>Application Status</h3>
                <p><strong>Current Status:</strong> <span className={`badge ${getStatusBadge(selectedApplication.status)}`}>{selectedApplication.status}</span></p>
                <p><strong>Applied Date:</strong> {new Date(selectedApplication.createdAt).toLocaleString()}</p>
              </div>
              <div className="detail-section">
                <h3>Update Status</h3>
                <div className="status-buttons mt-2 mb-1">
                  <button className="btn btn-secondary" onClick={() => { handleStatusUpdate(selectedApplication._id, 'reviewed'); setShowModal(false); }}>Mark as Reviewed</button>
                  <button className="btn btn-primary" onClick={() => { handleStatusUpdate(selectedApplication._id, 'shortlisted'); setShowModal(false); }}>Shortlist</button>
                  <button className="btn btn-success" onClick={() => { handleStatusUpdate(selectedApplication._id, 'offered'); setShowModal(false); }}>Offer</button>
                  <button className="btn btn-danger" onClick={() => { handleStatusUpdate(selectedApplication._id, 'rejected'); setShowModal(false); }}>Reject</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageApplications;
